// utils/conversationService.ts
import { Message } from '@/types/chat';
import { supabase } from './supabase';
import { leadSubmissionService, LeadSubmissionInput } from './leadSubmissionService';

export interface ConversationData {
  id: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

export interface ConversationListItem {
  id: string;
  created_at: string;
  updated_at: string;
}

// NOTE:
// - עוטפים לוגים ב-__DEV__ כדי שלא ידלוף מידע בפרודקשן
// - saveMessage לא זורק חריגות (כדי לא להפיל UX), מחזיר false במקרה כשל

const devLog = (...args: any[]) => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) console.log(...args);
};
const devWarn = (...args: any[]) => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) console.warn(...args);
};
const devError = (...args: any[]) => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) console.error(...args);
};

class ConversationService {
  private currentConversationId: string | null = null;
  private conversationPromise: Promise<string | null> | null = null;

  getCurrentConversationId(): string | null {
    return this.currentConversationId;
  }

  setCurrentConversation(conversationId: string): void {
    this.currentConversationId = conversationId;
    this.conversationPromise = null;
    devLog('[Conversation] 🔄 Set current conversation to:', conversationId);
  }

  clearCurrentConversation(): void {
    this.currentConversationId = null;
    this.conversationPromise = null;
    devLog('[Conversation] 🗑️ Cleared current conversation ID');
  }

  async getCurrentOrCreateConversation(): Promise<string | null> {
    if (this.currentConversationId) return this.currentConversationId;
    if (this.conversationPromise) return this.conversationPromise;

    this.conversationPromise = this.fetchOrCreateConversation();

    try {
      const conversationId = await this.conversationPromise;
      return conversationId;
    } finally {
      this.conversationPromise = null;
    }
  }

  private async fetchOrCreateConversation(): Promise<string | null> {
    try {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();

      if (userErr) {
        devError('[Conversation] ❌ Error getting user:', userErr);
        return null;
      }

      if (!user) {
        devWarn('[Conversation] ⚠️ No authenticated user');
        return null;
      }

      const { data: existingConversation, error: fetchError } = await supabase
        .from('conversations')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        devError('[Conversation] ❌ Error fetching conversation:', fetchError);
        return null;
      }

      if (existingConversation?.id) {
        devLog('[Conversation] ✅ Found existing conversation:', existingConversation.id);
        this.currentConversationId = existingConversation.id;
        return existingConversation.id;
      }

      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .insert({ user_id: user.id })
        .select('id')
        .single();

      if (createError) {
        devError('[Conversation] ❌ Error creating conversation:', createError);
        return null;
      }

      if (!newConversation?.id) {
        devError('[Conversation] ❌ Created conversation missing id');
        return null;
      }

      devLog('[Conversation] ✅ Created new conversation:', newConversation.id);
      this.currentConversationId = newConversation.id;
      return newConversation.id;
    } catch (error) {
      devError('[Conversation] ❌ Exception:', error);
      return null;
    }
  }

  async createNewConversation(): Promise<string | null> {
    try {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();

      if (userErr) {
        devError('[Conversation] ❌ Error getting user:', userErr);
        return null;
      }

      if (!user) {
        devWarn('[Conversation] ⚠️ No authenticated user');
        return null;
      }

      const { data: newConversation, error } = await supabase
        .from('conversations')
        .insert({ user_id: user.id })
        .select('id')
        .single();

      if (error) {
        devError('[Conversation] ❌ Error creating new conversation:', error);
        return null;
      }

      if (!newConversation?.id) {
        devError('[Conversation] ❌ Created conversation missing id');
        return null;
      }

      devLog('[Conversation] ✅ Created fresh conversation:', newConversation.id);
      this.currentConversationId = newConversation.id;
      this.conversationPromise = null;
      return newConversation.id;
    } catch (error) {
      devError('[Conversation] ❌ Exception creating conversation:', error);
      return null;
    }
  }

  async getAllConversations(): Promise<ConversationListItem[]> {
    try {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();

      if (userErr) {
        devError('[Conversation] ❌ Error getting user:', userErr);
        return [];
      }

      if (!user) {
        devWarn('[Conversation] ⚠️ No authenticated user');
        return [];
      }

      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('id, created_at, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        devError('[Conversation] ❌ Error fetching conversations:', error);
        return [];
      }

      return conversations || [];
    } catch (error) {
      devError('[Conversation] ❌ Exception fetching conversations:', error);
      return [];
    }
  }

  async loadMessages(): Promise<Message[]> {
    try {
      const conversationId = await this.getCurrentOrCreateConversation();

      if (!conversationId) {
        devLog('[Conversation] ℹ️ No conversation ID, returning empty messages');
        return [];
      }

      return this.loadConversationById(conversationId);
    } catch (error) {
      devError('[Conversation] ❌ Exception loading messages:', error);
      return [];
    }
  }

  async loadConversationById(conversationId: string): Promise<Message[]> {
    try {
      if (!conversationId) return [];

      devLog('[Conversation] 📥 Loading conversation:', conversationId);

      const { data: messagesData, error } = await supabase
        .from('messages')
        .select('role, content, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        devError('[Conversation] ❌ Error loading messages:', error);
        return [];
      }

      if (!messagesData || messagesData.length === 0) {
        devLog('[Conversation] ℹ️ No messages found in conversation');
        this.setCurrentConversation(conversationId); // עדיין מעדכנים את ה-current
        return [];
      }

      const messages: Message[] = messagesData.map((msg, index) => ({
        id: `loaded-${conversationId}-${index}`,
        role: msg.role as 'user' | 'assistant',
        content: (msg.content ?? '').toString(),
        timestamp: new Date(msg.created_at).getTime(),
      }));

      this.setCurrentConversation(conversationId);
      devLog('[Conversation] ✅ Loaded', messages.length, 'messages from conversation');
      return messages;
    } catch (error) {
      devError('[Conversation] ❌ Exception loading conversation:', error);
      return [];
    }
  }

  async saveMessage(role: 'user' | 'assistant', content: string): Promise<boolean> {
    try {
      const conversationId = await this.getCurrentOrCreateConversation();

      if (!conversationId) {
        devWarn('[Conversation] ⚠️ Cannot save message: no conversation ID');
        return false;
      }

      const safeContent = (content ?? '').toString();

      const { error: messageError } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        role,
        content: safeContent,
      });

      if (messageError) {
        devError('[Conversation] ❌ Error saving message:', messageError);
        return false;
      }

      // best-effort בלבד
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      if (updateError) {
        devWarn('[Conversation] ⚠️ Error updating conversation timestamp:', updateError);
      }

      return true;
    } catch (error) {
      devError('[Conversation] ❌ Exception saving message:', error);
      return false;
    }
  }

  async submitLead(payload: LeadSubmissionInput): Promise<{ success: boolean; error?: string }> {
    return leadSubmissionService.submitLead(payload);
  }

  async submitLeadGate(payload: LeadSubmissionInput): Promise<{ success: boolean; error?: string }> {
    return leadSubmissionService.submitLead(payload);
  }

  async createLead(payload: LeadSubmissionInput): Promise<{ success: boolean; error?: string }> {
    return leadSubmissionService.submitLead(payload);
  }
}

export const conversationService = new ConversationService();
